import { createApp } from 'vue'
import VueAxios from 'vue-axios'
import App from './App.vue'
import router from './router'
import store from './store/index'
import Ls from './services/ls'
import axios from 'axios'
import Helper from './mixins/Helper'
import Permissions from './mixins/Permissions'
import ModuleAccess from './mixins/ModuleAccess.vue'
import { BASE_URL } from './services/api'
import i18n from './plugins/i18n'
import { Vue3ProgressPlugin } from '@marcoschulte/vue3-progress'
import { useProgress } from '@marcoschulte/vue3-progress'
import { useHasAbility } from './composables/hasAbility.js'
import './echo'
import { loadPlatformColor } from '@/composables/usePlatformColor'
import { applyBranding, applyCachedBranding, BACKOFFICE_BRANDING } from '@/composables/useBranding'

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
axios.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded'
axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*'
axios.defaults.baseURL = BASE_URL

window.axios = axios

// Écran tactile : toastr met le message en pause tant que le « pointeur » est dessus (survol).
// Sur mobile, le doigt qui vient de toucher un bouton déclenche ce survol, sans jamais le
// quitter : le message ne disparaissait plus tant qu'on ne le touchait pas. On le referme donc
// nous-mêmes à la fin de son délai (les erreurs aussi, avec un délai plus long).
if (window.toastr && window.matchMedia && window.matchMedia('(hover: none)').matches) {
    ['success', 'info', 'warning', 'error'].forEach((type) => {
        const show = window.toastr[type].bind(window.toastr)
        window.toastr[type] = (message, title, options = {}) => {
            const $toast = show(message, title, options)
            const delay = options.timeOut ?? (type === 'error' ? 8000 : window.toastr.options.timeOut || 5000)
            if ($toast && delay > 0) {
                setTimeout(() => window.toastr.clear($toast, { force: true }), delay + 500)
            }
            return $toast
        }
    })
}
window.Ls = Ls
window.i18n = i18n

const progresses = []

axios.interceptors.request.use(function (config) {
    progresses.push(useProgress().start())
    const AUTH_TOKEN = Ls.get('auth.token')
    if (AUTH_TOKEN) {
        config.headers.common.Authorization = `Bearer ${AUTH_TOKEN}`
    }
    const shopId = store.getters['user/currentShop']?.id
        ?? store.getters['user/currentUser']?.shop_id
    if (shopId) {
        config.headers.common['X-Shop-Id'] = shopId
    }
    // Boutique gérée explicitement par l'écran (détail de boutique, sélecteur de
    // tarifs...) : vérifiée côté serveur par shopParamMatch, distincte de la
    // boutique active qui ne concerne que l'interface de vente.
    // Back-office personnalisé : l'API renvoie alors les images par défaut (produit, pack,
    // promotion, catégorie) à la couleur de la boutique au lieu des images neutres
    if (BACKOFFICE_BRANDING) {
        config.headers.common['X-Backoffice-Branding'] = '1'
    }
    if (window.managedShopId) {
        config.headers.common['X-Managed-Shop'] = window.managedShopId
    } else {
        delete config.headers.common['X-Managed-Shop']
    }
    // Le backend lit ce header (middleware Localization) pour App::setLocale()
    // et traduire les textes générés côté serveur (rapports, notifications...).
    // Sans lui, App::getLocale() reste bloqué sur le défaut Laravel quelle
    // que soit la langue choisie dans l'admin.
    config.headers.common['X-localization'] = i18n.global.locale
    return config
}, function (error) {
    progresses.pop()?.finish()
    return Promise.reject(error)
})

// Points rouges des boutiques : après une action qui touche le stock, un transfert ou un
// crédit (validation, règlement, entrée, inventaire, vente…), on les recalcule tout de suite
// au lieu d'attendre la minute suivante (regroupé si plusieurs requêtes se suivent).
const SHOP_ALERT_WRITES = /shop|store|output|entr|inventor|credit|checkout|product|stock/i
let shopAlertsRefresh = null
axios.interceptors.response.use(function (response) {
    const method = (response.config?.method || 'get').toLowerCase()
    if (method !== 'get' && SHOP_ALERT_WRITES.test(response.config?.url || '')) {
        clearTimeout(shopAlertsRefresh)
        shopAlertsRefresh = setTimeout(() => store.dispatch('shop/loadShopAlerts', { force: true }), 800)
    }
    return response
})

axios.interceptors.response.use(undefined, function (err) {
    progresses.pop()?.finish()
    // requête avec { silent: true } : l'écran affiche lui-même l'erreur (pas de toastr)
    if (err.config?.silent && err.response) {
        return Promise.reject(err)
    }
    if (!err.response) {
        window.toastr.error('Network error: Please check your internet connection.')
        return Promise.reject(err)
    }

    const status = err.response.status
    const data = err.response.data

    const LICENSE_CODES = ['no_subscription', 'subscription_expired', 'trial_expired', 'shop_inactive', 'shop_offline', 'pending_validation']

    if (status === 401 || data === 'Unauthorized.') {
        window.toastr.error(data?.message ?? 'Unauthorized')
        store.dispatch('auth/logout', true)
    } else if (status === 403 && LICENSE_CODES.includes(data?.code)) {
        const current = router.currentRoute.value
        // jamais pendant le choix / paiement de l'offre : c'est justement la sortie de ce blocage
        if (!['subscription-required', 'subscription-plans'].includes(current.name)) {
            router.push({ name: 'subscription-required', query: { code: data.code, message: data.message } })
        }
    } else if (status === 429) {
        // trop de requêtes en peu de temps : un seul message, pas un par requête refusée
        if (!window.__tooManyShownAt || Date.now() - window.__tooManyShownAt > 10000) {
            window.__tooManyShownAt = Date.now()
            window.toastr.warning(i18n.global.t('http_errors.too_many_requests'))
        }
    } else if (status === 422 && data?.code === 'session_not_active') {
        // Pas une erreur : aucune session de caisse ouverte. Rien sur les pages des sessions
        // (on est en train d'en ouvrir une, ou un rafraîchissement de la caisse en arrière-plan) ;
        // ailleurs, une information, une seule fois, avec le chemin pour ouvrir une session.
        const current = router.currentRoute.value.name
        if (!['pos-session-path', 'pos-session-create-path'].includes(current) && !window.__sessionInfoOpen && window.Swal) {
            window.__sessionInfoOpen = true
            window.Swal.fire({
                icon: 'info',
                title: i18n.global.t('pos_session_info.title'),
                text: i18n.global.t('pos_session_info.text'),
                showCancelButton: true,
                confirmButtonText: i18n.global.t('pos_session_info.open'),
                cancelButtonText: i18n.global.t('pos_session_info.later'),
            }).then((result) => {
                window.__sessionInfoOpen = false
                if (result.isConfirmed) router.push({ name: 'pos-session-create-path' })
            })
        }
    } else if (status === 422) {
        const msg = data?.message
        if (typeof msg === 'object') {
            window.toastr.error(Object.values(msg).join(', '))
        } else {
            window.toastr.error(msg ?? i18n.global.t('main.error_try'))
        }
    } else if (data?.errors) {
        Object.values(data.errors).forEach(e => window.toastr.error(e))
    } else if (data === 'Your account has been blocked') {
        window.toastr.error('Your account has been blocked')
        store.dispatch('auth/logout', true)
    } else if (status !== 403 && status !== 404) {
        window.toastr.error(data?.message ?? 'Unknown error occurred')
    }

    return Promise.reject(err)
})

$.ajaxPrefilter(function (options) {
    if (options.url.includes('cdn.datatables.net')) {}
})

// Même annonce pour les tableaux (DataTables), qui appellent l'API par jQuery et non par axios
if (BACKOFFICE_BRANDING) {
    $.ajaxPrefilter(function (options, originalOptions, jqXHR) {
        if (String(options.url || '').startsWith(BASE_URL)) {
            jqXHR.setRequestHeader('X-Backoffice-Branding', '1')
        }
    })
}

// Couleurs du back-office posées avant le premier affichage, à partir de la dernière visite
// (pas de couleur d'origine qui clignote le temps des appels API) :
// - connecté au back-office : couleurs de la boutique si la personnalisation est activée
//   (voir LayoutBasic), sinon couleurs par défaut de la plateforme ;
// - connecté au Landlord seulement : couleurs par défaut de la plateforme.
const applyBootColors = () => {
    if (Ls.get('auth.token')) {
        BACKOFFICE_BRANDING ? applyCachedBranding() : applyBranding({})
    } else if (Ls.get('landlord.token')) {
        applyBranding({})
    }
}

// Couleurs de la plateforme (Landlord) : celles gardées tout de suite, puis celles de l'API —
// réappliquées si elles ont changé depuis la dernière visite
applyBootColors()
loadPlatformColor().then(applyBootColors)

window.vueJs = createApp(App)
    .use(store)
    .use(i18n)
    .mixin(Helper)
    .mixin(Permissions)
    .mixin(ModuleAccess)
    .mixin({
        methods: {
            $can: useHasAbility().can,
            $canInActiveShop: useHasAbility().canInActiveShop
        }
    })
    .use(router)
    .use(VueAxios, axios)
    .use(Vue3ProgressPlugin)
    .mount('#app')

// Installation de l'application : le navigateur ne propose l'événement qu'une fois, souvent avant
// que l'en-tête soit affiché — on le garde ici pour le bouton « Installer » (PwaInstallButton)
window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    window.__pwaInstallEvent = event
    window.dispatchEvent(new Event('pwa-install-available'))
})
window.addEventListener('appinstalled', () => {
    window.__pwaInstallEvent = null
    window.dispatchEvent(new Event('pwa-installed'))
})

// Le SW est toujours enregistré (installation PWA). L'abonnement aux notifications se fait
// une fois connecté (LayoutBasic) : au lancement, on est souvent encore sur la connexion.
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js')
        .catch((err) => {
            if (process.env.NODE_ENV === 'development') {
                console.error('Service Worker registration failed:', err)
            }
        })
}
